import React, { Fragment } from 'react';
import { Redirect, Route, withRouter } from 'react-router';
import { Switch } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { compose, mapProps, withProps } from 'recompose';
import { isDefinedAndNotNull } from '@/se/utilities/check';
import Page from '@/se/components/Page';
import Sidebar from '@/se/components/Sidebar';
import compact from 'lodash/compact';
import ChangePassword from '../ChangePassword';
import Gateways from '../../gateway/Gateways';
import { dark as darkTheme } from '../../../../theme';
import ThemeBasedCSS from '../../../../components/ThemeBasedCSS';
import Patients from '../../../entities/patient/ActiveAndPastPatients';
import ProcedureTypes from '../../../entities/procedure-type/ProcedureTypes';
import Users from '../../../entities/user/Users';
import Physicians from '../../../entities/physician/Physicians';
import Screens from '../../../entities/screens/Screens';
import { unpackSessionObject } from '../../unpackSessionObject';
import MinewTestUI from '../../minew';
import ReplaySignal from '../../replay';
import RulesConfiguration from '../../rules/RulesConfiguration';
import Tracker from '../../../tracker';
import { withSession } from '../../../../state/Session';
import { Context as PatientNameFormatContext } from '../../../../contexts/PatientNameFormat';
import get from 'lodash/fp/get';
import StaffMembers from '../../../entities/staff/StaffMembers';
import Anesthesiologists from '../../../entities/anesthesiologist/Anesthesiologists';
import ScheduledProcedures from '../../../entities/procedures/ScheduledProcedures';
import SensorPage from '../../sensor/SensorPage';
import { muiThemeDark } from '../../../../muiTheme';
import { ThemeProvider as MUIThemeProvider } from '@material-ui/core/styles';
import Training from '../../training/Training';
import ScheduledRequests from '../../../entities/scheduleRequests/ScheduledRequests';
import { CssBaseline } from '@material-ui/core';
import { MainContainer } from '../../MainContainer';
import { OmniSearchProvider, OmniSearchSources } from '../../../OmniSearch';
import activeAndPastProceduresSource from '../../../../omnisearch/sources/hospital/activeAndPastProcedures';
import scheduledProceduresSource from '../../../../omnisearch/sources/hospital/scheduledProcedures';
import { CovidScreeningSC } from '../../../entities/covidScreening/CovidScreeningSubmissions';
import OpenPositions from '../../kiosk/openPositions/OpenPositions';
import OpenPositionCounter from '../../kiosk/openPositions/OpenPositionCounter';
import Vendors from '../../../entities/vendor/Vendors';
import { mdiNeedle, mdiPackageVariantClosed } from '@mdi/js';
import Icon from '@mdi/react';
import {
  AccessibilityNew,
  AirlineSeatFlat,
  EnhancedEncryption,
  Event,
  Healing,
  InvertColors,
  LocalHospital,
  MyLocation,
  People,
  Router,
  Schedule,
  Settings,
  ShoppingCart,
  Theaters,
  Today,
  Tune,
  Tv,
  VideoLabel,
} from '@material-ui/icons';
import Equipment from '../../../entities/equipment/Equipment';
import Header from '../admin/Header';
import Footer from '../admin/Footer';
import { withScope } from '../../../../contexts/ScopeContext';

const getSystemScreenRoutes = (basePath, baseUrl, organizationId, search, modules) =>
  compact([
    {
      path: `/kiosk/${organizationId}/monitor`,
      to: `/kiosk/${organizationId}/monitor`,
      target: '_blank',
      label: 'Operations Screen',
      icon: <VideoLabel />,
    },
    {
      path: `/kiosk/${organizationId}/schedule`,
      to: `/kiosk/${organizationId}/schedule`,
      label: 'OR Schedule Screen',
      icon: <Event />,
    },
    {
      path: `/kiosk/${organizationId}/waiting-room`,
      to: `/kiosk/${organizationId}/waiting-room`,
      target: '_blank',
      label: 'Waiting Room Screen',
      icon: <Tv />,
      hideOnMobile: true,
    },
    modules.blockSchedule
      ? {
          path: `/kiosk/${organizationId}/block-schedule`,
          to: `/kiosk/${organizationId}/block-schedule`,
          label: 'Block Schedule',
          icon: <Schedule />,
        }
      : undefined,
  ]);

const getAdminRoutes = (
  basePath,
  baseUrl,
  isSuperAdmin,
  isMonitoringEngineer,
  modules,
  organizationId,
  ospitekOrdersIntegrations
) =>
  [
    {
      separator: true,
    },
    {
      path: `${basePath}/patients`,
      to: `${baseUrl}/patients`,
      label: 'Patients',
      icon: <AccessibilityNew />,
      component: Patients,
    },
    modules.covidScreening && !isMonitoringEngineer
      ? {
          path: `${basePath}/covid-screening`,
          to: `${baseUrl}/covid-screening`,
          label: 'Covid Screening',
          icon: 'coronavirus',
          component: CovidScreeningSC,
        }
      : undefined,
    modules.patientRegistration && !isMonitoringEngineer
      ? {
          path: `${basePath}/scheduled-patients`,
          to: `${baseUrl}/scheduled-patients`,
          label: 'PreOp Registration',
          icon: <Schedule />,
          component: ScheduledProcedures,
        }
      : undefined,
    modules.acceptsProcedureTransfer
      ? {
          path: `${basePath}/scheduled-requests`,
          to: `${baseUrl}/scheduled-requests`,
          label: 'Schedule requests',
          icon: <Today />,
          component: ScheduledRequests,
        }
      : undefined,
    modules.temperatureReporting
      ? {
          path: `${basePath}/sensors`,
          to: `${baseUrl}/sensors`,
          label: 'Temp & Humidity',
          icon: <InvertColors />,
          component: SensorPage,
        }
      : undefined,
    {
      separator: true,
    },
    {
      label: 'Staffing',
      icon: <Settings />,
      NotificationComponent: OpenPositionCounter,
      subPages: [
        ...(!isMonitoringEngineer
          ? [
              {
                path: `${basePath}/positions`,
                to: `${baseUrl}/positions`,
                label: 'Open Positions',
                NotificationComponent: OpenPositionCounter,
                icon: <AccessibilityNew />,
                component: OpenPositions,
              },
              {
                path: `/kiosk/${organizationId}/calendar`,
                to: `/kiosk/${organizationId}/calendar`,
                target: '_blank',
                label: 'Calendar',
                icon: <Event />,
              },
            ]
          : []),
        {
          path: `${basePath}/physicians`,
          to: `${baseUrl}/physicians`,
          label: 'Physicians',
          icon: <LocalHospital />,
          component: Physicians,
        },
        {
          path: `${basePath}/staff`,
          to: `${baseUrl}/staff`,
          label: 'Staff Members',
          icon: <EnhancedEncryption />,
          component: StaffMembers,
        },
        {
          path: `${basePath}/anesthesiologists`,
          to: `${baseUrl}/anesthesiologists`,
          label: 'Anesthesiologists',
          icon: <AirlineSeatFlat />,
          component: Anesthesiologists,
        },
        {
          path: `${basePath}/vendors`,
          to: `${baseUrl}/vendors`,
          label: 'Vendors',
          icon: <Icon path={mdiPackageVariantClosed} title="Vendors" size={1} color="#fff" />,
          component: Vendors,
        },
      ],
    },
    {
      separator: true,
    },
    {
      label: 'Configuration',
      icon: <Settings />,
      subPages: [
        ...(!isMonitoringEngineer
          ? [
              {
                path: `${basePath}/procedure-types`,
                to: `${baseUrl}/procedure-types`,
                label: 'Procedure Types',
                icon: <Healing />,
                component: ProcedureTypes,
              },
              {
                path: `${basePath}/equipment`,
                to: `${baseUrl}/equipment`,
                label: 'Equipment',
                icon: <Icon path={mdiNeedle} size={'24px'} />,
                component: Equipment,
              },
              {
                path: `${basePath}/users`,
                to: `${baseUrl}/users`,
                label: 'Users',
                icon: <People />,
                component: Users,
              },
            ]
          : []),
        {
          path: `${basePath}/screens`,
          to: `${baseUrl}/screens`,
          label: 'Screen Management',
          icon: <Tv />,
          component: Screens,
        },
      ],
    },
    {
      separator: true,
    },
    modules.ospitekOrdersIntegrations
      ? {
          externalLink: ospitekOrdersIntegrations?.externalLink?.link,
          label: 'Orders',
          icon: <ShoppingCart />,
        }
      : undefined,
    {
      externalLink: 'https://isqbazmccrg.typeform.com/to/KXHRf1SS',
      label: 'Development Requests',
      icon: <ShoppingCart />,
    },
    isMonitoringEngineer && {
      path: `${basePath}/tracker`,
      to: `${baseUrl}/tracker`,
      label: 'Track Journey',
      icon: <MyLocation />,
      component: Tracker,
    },
    isMonitoringEngineer && {
      path: `${basePath}/gateways`,
      to: `${baseUrl}/gateways`,
      label: 'Gateways',
      icon: <Router />,
      component: Gateways,
    },
    !isSuperAdmin &&
      !isMonitoringEngineer && {
        path: `${basePath}/training`,
        to: `${baseUrl}/training`,
        label: 'Video Tutorials',
        icon: <Theaters />,
        component: Training,
      },
  ].filter(_ => !!_);

const getSuperAdminUserRoutes = (basePath, baseUrl) => [
  {
    separator: true,
  },
  {
    path: `${basePath}/tracker`,
    to: `${baseUrl}/tracker`,
    label: 'Track Journey',
    icon: <MyLocation />,
    component: Tracker,
  },
  {
    path: `${basePath}/gateways`,
    to: `${baseUrl}/gateways`,
    label: 'Gateways',
    icon: <Router />,
    component: Gateways,
  },
  {
    path: `${basePath}/rules`,
    to: `${baseUrl}/rules`,
    label: 'Transition Rules',
    icon: <Tune />,
    component: RulesConfiguration,
  },
];

const getSwitchRoutes = (
  basePath,
  baseUrl,
  isSuperAdmin,
  isMonitoringEngineer,
  modules,
  organizationId,
  ospitekOrdersIntegrations
) => {
  const adminRoutes = getAdminRoutes(
    basePath,
    baseUrl,
    isSuperAdmin,
    isMonitoringEngineer,
    modules,
    organizationId,
    ospitekOrdersIntegrations
  );
  if (isSuperAdmin) {
    const superAdminRoutes = getSuperAdminUserRoutes(basePath, baseUrl, modules);
    return [...adminRoutes, ...superAdminRoutes];
  }

  return adminRoutes;
};

const getBasePathAndUrl = match => {
  if (!isDefinedAndNotNull(match) || !isDefinedAndNotNull(!match.path) || !isDefinedAndNotNull(!match.url)) {
    return ['', ''];
  }
  return [match.path === '/' ? '' : match.path, match.url === '/' ? '' : match.url];
};

const AdminSidebar = compose(
  withSession(unpackSessionObject),
  withRouter,
  withScope,
  withProps({
    Header,
    links: [],
    Footer,
  }),
  mapProps(props => {
    const { organization, match, isSuperAdmin, isMonitoringEngineer, search } = props;
    const organizationId =
      (isSuperAdmin ? match.params['organizationId'] : organization) || match.params.organizationId;
    const [basePath, baseUrl] = getBasePathAndUrl(match);

    return {
      isSuperAdmin,
      ...props,
      links: compact([
        ...getSystemScreenRoutes(
          basePath,
          baseUrl,
          organizationId,
          search,
          Object.assign(
            {},
            {
              covidScreening: !!props.scope?.hospital?.covidScreening,
            },
            get('scope.hospital.modules')(props)
          )
        ),
        ...getSwitchRoutes(
          basePath,
          baseUrl,
          isSuperAdmin,
          isMonitoringEngineer,
          Object.assign(
            {},
            {
              covidScreening: !!props.scope?.hospital?.covidScreening,
              ospitekOrdersIntegrations: !!props.scope?.hospital?.ospitekOrdersIntegrations?.externalLink?.enabled,
            },
            get('scope.hospital.modules')(props)
          ),
          organizationId,
          props.scope?.hospital?.ospitekOrdersIntegrations
        ),
      ]),
    };
  })
)(Sidebar);

const Homepage = props => {
  const [basePath, baseUrl] = getBasePathAndUrl(props.match);
  const modules = {
    patientRegistration: true,
    temperatureReporting: true,
    acceptsProcedureTransfer: true,
    covidScreening: !!props.scope?.hospital?.covidScreening,
  };
  const switchRoutes = compact(
    getSwitchRoutes(basePath, baseUrl, props.isSuperAdmin, props.isMonitoringEngineer, modules)
  ).filter(_ => !_.separator);
  return (
    <ThemeProvider theme={darkTheme}>
      <MUIThemeProvider theme={muiThemeDark}>
        <style>
          {`body {
            background: ${muiThemeDark.palette.background.default};
          }`}
        </style>
        <CssBaseline />
        <PatientNameFormatContext.Provider
          value={props.isSuperAdmin ? 'PatientId' : props.patientNameFormat || 'FullName'}
        >
          <OmniSearchProvider>
            <OmniSearchSources sources={[activeAndPastProceduresSource, scheduledProceduresSource]} />
            <Fragment>
              <Page Sidebar={AdminSidebar}>
                <MainContainer switchControl>
                  <Switch>
                    <Route exact path={`${basePath}/`} render={() => <Redirect to={`${baseUrl}/patients`} />} />
                    {switchRoutes.map(({ path, component, subPages }, i) => [
                      path ? <Route key={i} path={path} component={component} /> : null,
                      ...(subPages
                        ? subPages.map(subPage => (
                            <Route
                              key={path ? `${path}${subPage.path}` : subPage.path}
                              path={path ? `${path}${subPage.path}` : subPage.path}
                              component={subPage.component}
                            />
                          ))
                        : []),
                    ])}
                    <Route path="/change-password" component={ChangePassword} />
                    {props.isSuperAdmin && <Route path={`${basePath}/tools/minew`} component={MinewTestUI} />}
                    {props.isSuperAdmin && <Route path={`${basePath}/tools/replay`} component={ReplaySignal} />}
                    <Route render={() => <Redirect to={`${baseUrl}/patients`} />} />
                  </Switch>
                </MainContainer>
              </Page>
              <ThemeBasedCSS />
            </Fragment>
          </OmniSearchProvider>
        </PatientNameFormatContext.Provider>
      </MUIThemeProvider>
    </ThemeProvider>
  );
};

export default compose(
  withSession(unpackSessionObject),
  withRouter,
  withSession(get('session.account.metadata')),
  withScope
)(Homepage);
